"use client";

import { useState, useMemo } from "react";
import { Search, Book, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { GlossaryTerm } from "@/types";

interface GlossaryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  terms: GlossaryTerm[];
}

export function GlossaryModal({ open, onOpenChange, title, terms }: GlossaryModalProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter and sort terms
  const filteredTerms = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    
    if (!query) {
      return terms;
    }
    
    return terms.filter(
      (term) =>
        term.term.toLowerCase().includes(query) ||
        term.definition.toLowerCase().includes(query)
    );
  }, [terms, searchQuery]);

  // Group terms by first letter
  const groupedTerms = useMemo(() => {
    const groups: Record<string, GlossaryTerm[]> = {};
    
    filteredTerms.forEach((term) => {
      const firstLetter = term.term.charAt(0).toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(term);
    });
    
    // Sort groups alphabetically
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredTerms]);

  // Reset search when modal closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchQuery("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Book className="h-5 w-5" />
            {title || "Glossaire"}
          </DialogTitle>
        </DialogHeader>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un terme..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        
        {/* Terms List */}
        <div className="flex-1 min-h-0 max-h-[calc(80vh-180px)] -mx-6 px-6 overflow-y-auto">
          {filteredTerms.length === 0 ? (
            <div className="text-center py-12">
              <Book className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              {searchQuery ? (
                <>
                  <p className="text-muted-foreground">
                    Aucun terme trouvé pour « {searchQuery} »
                  </p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Essayez avec d&apos;autres mots-clés
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground">
                  Aucun terme dans le glossaire
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-6 pb-4">
              {groupedTerms.map(([letter, letterTerms]) => (
                <div key={letter}>
                  {/* Letter Header */}
                  <div className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-10 py-2 mb-2">
                    <span className="text-lg font-bold text-primary">
                      {letter}
                    </span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({letterTerms.length} terme{letterTerms.length > 1 ? "s" : ""})
                    </span>
                  </div>
                  
                  {/* Terms in this letter group */}
                  <div className="space-y-3">
                    {letterTerms.map((term) => (
                      <div
                        key={term.id}
                        className="p-4 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                      >
                        <h4 className="font-semibold text-foreground">
                          {highlightMatch(term.term, searchQuery)}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                          {highlightMatch(term.definition, searchQuery)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer with count */}
        {filteredTerms.length > 0 && (
          <div className="text-xs text-muted-foreground text-center pt-2 border-t">
            {searchQuery
              ? `${filteredTerms.length} terme${filteredTerms.length > 1 ? "s" : ""} trouvé${filteredTerms.length > 1 ? "s" : ""}`
              : `${terms.length} terme${terms.length > 1 ? "s" : ""} au total`}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Helper function to highlight search matches
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) {
    return text;
  }
  
  const regex = new RegExp(`(${escapeRegExp(query)})`, "gi");
  const parts = text.split(regex);
  
  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} className="bg-yellow-200 dark:bg-yellow-900 rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

// Helper to escape special regex characters
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
